import {
  useState,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useTenant } from '../../../contexts/TenantContext';
import { getTenantIdFromUrl } from '../../../api/apiUtils';
import { SurveySection } from './SurveySection';
import { RadioGroup } from './RadioGroup';
import { CheckboxGroup } from './CheckboxGroup';
import {
  getActiveSurveyTemplate,
  type SurveyTemplate,
  type SurveyFieldConfig,
} from '../../../api/surveyTemplateClient';

/** 表單送出形態；memberId、phone 由父層從 ensure 會員與選填電話帶入 */
interface SurveyData {
  templateId: number;
  memberId?: string;
  phone?: string;
  [key: string]: unknown;
}

interface DynamicSurveyFormProps {
  onSubmit: (data: SurveyData) => Promise<void>;
  isSubmitting: boolean;
  onTemplateLoad?: (template: SurveyTemplate) => void; // 回傳載入的模板給父組件
}

/**
 * DynamicSurveyForm 元件
 * 根據問卷模板動態渲染表單
 */
export function DynamicSurveyForm({
  onSubmit,
  isSubmitting,
  onTemplateLoad,
}: DynamicSurveyFormProps) {
  console.log('[DynamicSurveyForm] ========== 組件開始渲染 ==========');
  console.log('[DynamicSurveyForm] Props:', {
    hasOnSubmit: !!onSubmit,
    isSubmitting,
    hasOnTemplateLoad: !!onTemplateLoad,
  });
  const { tenantId, isReady: tenantReady } = useTenant();
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]); // 表單驗證錯誤
  const hasLoadedRef = useRef(false); // 追蹤是否已經載入過

  // 載入問卷模板（memberId、phone 由父層 Survey App 以 ensure 會員與選填電話帶入，不在此填寫）
  const loadTemplate = useCallback(async () => {
    console.log('[DynamicSurveyForm] ========== loadTemplate 開始 ==========');
    console.log('[DynamicSurveyForm] 當前狀態:', {
      tenantId,
      tenantReady,
      hasLoaded: hasLoadedRef.current,
      currentLoading: loading,
      currentError: error,
    });

    try {
      setLoading(true);
      console.log('[DynamicSurveyForm] 調用 getActiveSurveyTemplate...');
      console.log('[DynamicSurveyForm] API 調用前檢查:', {
        apiBase:
          typeof window !== 'undefined'
            ? import.meta.env.VITE_API_BASE_URL
            : 'N/A',
        tenantIdFromUrl:
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('tenantId')
            : 'N/A',
      });

      const startTime = Date.now();
      const activeTemplate = await getActiveSurveyTemplate();
      const duration = Date.now() - startTime;

      console.log('[DynamicSurveyForm] API 調用完成:', {
        success: !!activeTemplate,
        duration: `${duration}ms`,
        templateId: activeTemplate?.id,
        templateTitle: activeTemplate?.title,
        hasConfig: !!activeTemplate?.config,
        fieldsCount: activeTemplate?.config?.fields?.length || 0,
      });

      if (!activeTemplate) {
        console.warn('[DynamicSurveyForm] 找不到啟用的問卷模板（404 或 null）');
        setError('目前沒有問卷');
        setLoading(false);
        return;
      }

      console.log('[DynamicSurveyForm] 設定模板並回傳給父組件');
      console.log('[DynamicSurveyForm] 模板詳細資訊:', {
        id: activeTemplate.id,
        title: activeTemplate.title,
        hasConfig: !!activeTemplate.config,
        hasFields: !!activeTemplate.config?.fields,
        fieldsCount: activeTemplate.config?.fields?.length || 0,
        hasSections: !!activeTemplate.config?.sections,
        sectionsCount: activeTemplate.config?.sections?.length || 0,
      });
      setTemplate(activeTemplate);
      console.log('[DynamicSurveyForm] ✅ template 狀態已更新');

      // 回傳模板給父組件（用於顯示標題）
      if (onTemplateLoad) {
        console.log('[DynamicSurveyForm] 回傳模板給父組件');
        onTemplateLoad(activeTemplate);
      }

      // 初始化表單資料（支援新舊格式）
      const initialData: Record<string, unknown> = {};
      const fields = getFieldsFromConfig(activeTemplate.config);
      console.log(
        '[DynamicSurveyForm] 初始化表單資料，欄位數量:',
        fields.length
      );

      // 檢查模板中是否有 phone 欄位
      const hasPhoneField = fields.some(field => {
        const fieldName = field.id || field.name || '';
        return fieldName === 'phone' || field.type === 'phone';
      });

      // 若模板無 phone 欄位則不預填；電話改由會員中心填寫，問卷不要求必填
      if (!hasPhoneField) {
        initialData.phone = '';
      }

      fields.forEach(field => {
        const fieldName = field.id || field.name || '';
        if (field.type === 'checkbox') {
          initialData[fieldName] = [];
        } else if (field.type === 'rating') {
          initialData[fieldName] = '';
        } else {
          initialData[fieldName] = '';
        }
      });
      setFormData(initialData);
      console.log('[DynamicSurveyForm] 模板載入完成，表單資料已初始化');
      setLoading(false); // 成功时也要设置 loading = false
      console.log('[DynamicSurveyForm] loadTemplate 成功完成, loading = false');
    } catch (err) {
      console.error(
        '[DynamicSurveyForm] ========== 載入問卷模板失敗 =========='
      );
      console.error('[DynamicSurveyForm] 錯誤詳情:', {
        error: err,
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        tenantId,
        tenantReady,
      });
      setError(err instanceof Error ? err.message : '載入問卷模板失敗');
      setLoading(false); // 错误时也要设置 loading = false
      console.log('[DynamicSurveyForm] loadTemplate 失敗完成, loading = false');
    }
    console.log('[DynamicSurveyForm] ========== loadTemplate 完成 ==========');
  }, [onTemplateLoad, tenantId, tenantReady]);

  // 必須等待 tenantId 準備好才發送請求
  useEffect(() => {
    console.log('[DynamicSurveyForm] ========== useEffect 觸發 ==========');

    // 嘗試從 TenantContext 獲取 tenantId，如果沒有則使用 getTenantIdFromUrl() 作為 fallback
    const effectiveTenantId = tenantId || getTenantIdFromUrl();
    const effectiveTenantReady = tenantReady || !!effectiveTenantId;

    console.log('[DynamicSurveyForm] useEffect 依賴項狀態:', {
      tenantReady,
      tenantId,
      effectiveTenantId,
      effectiveTenantReady,
      hasLoaded: hasLoadedRef.current,
      loadTemplateFunction: typeof loadTemplate === 'function',
    });

    // 如果 tenantId 還沒準備好（包括 fallback），不執行
    if (!effectiveTenantReady || !effectiveTenantId) {
      console.log('[DynamicSurveyForm] ⏸️ tenantId 未準備好，跳過載入:', {
        tenantReady,
        tenantId,
        effectiveTenantId,
        effectiveTenantReady,
        reason: !effectiveTenantReady
          ? 'effectiveTenantReady = false'
          : 'effectiveTenantId = null/undefined',
      });
      return;
    }

    // 如果已經載入過，不重複載入
    if (hasLoadedRef.current) {
      console.log('[DynamicSurveyForm] ⏭️ 已經載入過，跳過重複載入');
      return;
    }

    console.log(
      '[DynamicSurveyForm] ✅ 條件滿足，開始載入模板... (使用 tenantId:',
      effectiveTenantId,
      ')'
    );
    console.log('[DynamicSurveyForm] 設置 hasLoadedRef.current = true');
    hasLoadedRef.current = true;
    loadTemplate();
  }, [tenantReady, tenantId, loadTemplate]);

  // 從 config 取得欄位列表（支援新舊格式）
  const getFieldsFromConfig = (
    config: SurveyTemplate['config']
  ): SurveyFieldConfig[] => {
    // 優先使用扁平化 fields
    if (config.fields && config.fields.length > 0) {
      return config.fields;
    }
    // 否則使用 sections（向後兼容）
    if (config.sections && config.sections.length > 0) {
      return config.sections.flatMap(section => section.fields);
    }
    return [];
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!template) {
      return;
    }

    // 清除之前的驗證錯誤
    setValidationErrors([]);

    // 驗證必填欄位（支援新舊格式）
    const templateFields = getFieldsFromConfig(template.config);
    const missingFields: string[] = [];

    // 驗證模板中的必填欄位
    templateFields.forEach(field => {
      const fieldName = field.id || field.name || '';
      if (field.required) {
        const value = formData[fieldName];
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          missingFields.push(field.label || fieldName);
        }
      }
    });

    // 合併驗證錯誤（電話、memberId 改由會員中心填寫，問卷不要求必填）
    const allErrors: string[] = [];
    if (missingFields.length > 0) {
      allErrors.push(`請填寫所有必填欄位：${missingFields.join('、')}`);
    }

    if (allErrors.length > 0) {
      setValidationErrors(allErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 準備提交：只送 templateId 與問卷欄位；memberId、phone 由父層帶入
    const {
      templateId: _t,
      memberId: _m,
      phone: _p,
      ...responseData
    } = formData;

    const submitData = {
      templateId: template.id!,
      ...responseData,
    };

    await onSubmit(submitData as SurveyData);
  };

  const renderField = (field: SurveyFieldConfig, questionNumber?: number) => {
    try {
      const fieldName = field.id || field.name || '';
      if (!fieldName) {
        console.warn('[DynamicSurveyForm] 欄位缺少 id 和 name:', field);
        return null;
      }

      if (!field.type) {
        console.warn('[DynamicSurveyForm] 欄位缺少 type:', field);
        return null;
      }

      const rawValue = formData[fieldName];
      const value =
        field.type === 'checkbox'
          ? Array.isArray(rawValue)
            ? rawValue
            : []
          : String(rawValue || '');
      const questionPrefix =
        questionNumber !== undefined ? `${questionNumber}. ` : '';

      switch (field.type) {
        case 'phone':
        case 'text':
        case 'email': {
          // 手機號碼欄位特殊處理：自動格式化並驗證
          const isPhoneField = field.type === 'phone' || fieldName === 'phone';
          const phonePattern = isPhoneField
            ? '^09\\d{8}$'
            : field.validation?.pattern;
          const phonePlaceholder = isPhoneField
            ? '請輸入手機號碼（例如：0912345678）'
            : field.placeholder;

          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type={
                  field.type === 'email'
                    ? 'email'
                    : field.type === 'phone'
                      ? 'tel'
                      : 'text'
                }
                placeholder={phonePlaceholder}
                value={String(value)}
                onChange={e => {
                  let newValue = e.target.value;
                  // 手機號碼欄位：只允許數字，最多 10 位
                  if (isPhoneField) {
                    newValue = newValue.replace(/\D/g, '').slice(0, 10);
                  }
                  setFormData({ ...formData, [fieldName]: newValue });
                  // 清除驗證錯誤（當用戶開始輸入時）
                  if (validationErrors.length > 0) {
                    setValidationErrors([]);
                  }
                }}
                required={field.required}
                maxLength={isPhoneField ? 10 : field.maxLength}
                pattern={phonePattern}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              {isPhoneField && (
                <p className="text-sm text-gray-500 mt-1">10位數字，以09開頭</p>
              )}
              {!isPhoneField && field.validation?.message && (
                <p className="text-sm text-gray-500 mt-1">
                  {field.validation.message}
                </p>
              )}
            </div>
          );
        }

        case 'number': {
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                placeholder={field.placeholder}
                value={String(value)}
                onChange={e =>
                  setFormData({ ...formData, [fieldName]: e.target.value })
                }
                required={field.required}
                min={field.validation?.min}
                max={field.validation?.max}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              {field.validation?.message && (
                <p className="text-sm text-gray-500 mt-1">
                  {field.validation.message}
                </p>
              )}
            </div>
          );
        }

        case 'rating': {
          // rating 類型使用 radio 渲染，但樣式不同
          const ratingOptions = Array.isArray(field.options)
            ? field.options.map(opt => {
                if (typeof opt === 'string') {
                  return { value: opt, label: opt };
                }
                return opt;
              })
            : [];
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-2">
                {ratingOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-gray-300 cursor-pointer hover:border-line-green transition-colors"
                    style={{
                      backgroundColor:
                        String(value) === option.value
                          ? 'var(--primary-color, #2563eb)'
                          : 'transparent',
                      borderColor:
                        String(value) === option.value
                          ? 'var(--primary-color, #2563eb)'
                          : undefined,
                      color: String(value) === option.value ? 'white' : '#333',
                    }}
                  >
                    <input
                      type="radio"
                      name={fieldName}
                      value={option.value}
                      checked={String(value) === option.value}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          [fieldName]: e.target.value,
                        })
                      }
                      required={field.required}
                      className="sr-only"
                    />
                    <span className="text-lg font-bold">{option.value}</span>
                  </label>
                ))}
              </div>
              {field.validation?.message && (
                <p className="text-sm text-gray-500 mt-1">
                  {field.validation.message}
                </p>
              )}
            </div>
          );
        }

        case 'textarea': {
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <textarea
                placeholder={field.placeholder}
                value={String(value)}
                onChange={e =>
                  setFormData({ ...formData, [fieldName]: e.target.value })
                }
                required={field.required}
                rows={field.rows || 3}
                maxLength={field.maxLength}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none"
              />
              {field.maxLength && (
                <p className="text-sm text-gray-500 mt-1">
                  最多 {field.maxLength} 字元（目前 {String(value).length}{' '}
                  字元）
                </p>
              )}
            </div>
          );
        }

        case 'radio': {
          const radioOptions = Array.isArray(field.options)
            ? field.options.map(opt => {
                if (typeof opt === 'string') {
                  return { value: opt, label: opt };
                }
                return opt;
              })
            : [];
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <RadioGroup
                name={fieldName}
                options={radioOptions}
                value={String(value)}
                onChange={newValue =>
                  setFormData({ ...formData, [fieldName]: newValue })
                }
                required={field.required}
              />
            </div>
          );
        }

        case 'checkbox': {
          const checkboxOptions = Array.isArray(field.options)
            ? field.options.map(opt => {
                if (typeof opt === 'string') {
                  return { value: opt, label: opt };
                }
                return opt;
              })
            : [];
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <CheckboxGroup
                name={fieldName}
                options={checkboxOptions}
                values={Array.isArray(value) ? value : []}
                onChange={newValues =>
                  setFormData({ ...formData, [fieldName]: newValues })
                }
                required={field.required}
                showOther={field.showOther}
                otherValue={String(formData[`${fieldName}_other`] || '')}
                onOtherChange={otherValue =>
                  setFormData({
                    ...formData,
                    [`${fieldName}_other`]: otherValue,
                  })
                }
              />
            </div>
          );
        }

        case 'select': {
          const selectOptions = Array.isArray(field.options)
            ? field.options.map(opt => {
                if (typeof opt === 'string') {
                  return { value: opt, label: opt };
                }
                return opt;
              })
            : [];
          return (
            <div key={fieldName}>
              <label className="block text-gray-700 font-semibold mb-2">
                {questionPrefix}
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
              </label>
              <select
                value={String(value)}
                onChange={e =>
                  setFormData({ ...formData, [fieldName]: e.target.value })
                }
                required={field.required}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">請選擇</option>
                {selectOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        }

        default: {
          console.warn(
            '[DynamicSurveyForm] 不支援的欄位類型:',
            field.type,
            field
          );
          return (
            <div
              key={fieldName}
              className="p-2 bg-yellow-50 border border-yellow-200 rounded"
            >
              <p className="text-yellow-600 text-sm">
                不支援的欄位類型: {field.type} - {field.label || fieldName}
              </p>
            </div>
          );
        }
      }
    } catch (err) {
      console.error('[DynamicSurveyForm] renderField 錯誤:', err, field);
      const fieldName = field.id || field.name || 'unknown';
      return (
        <div
          key={fieldName}
          className="p-2 bg-red-50 border border-red-200 rounded"
        >
          <p className="text-red-600 text-sm">
            欄位渲染錯誤: {field.label || fieldName}
          </p>
          <p className="text-red-500 text-xs mt-1">
            {err instanceof Error ? err.message : String(err)}
          </p>
        </div>
      );
    }
  };

  // 取得欄位列表（必須在所有條件渲染之前調用 hooks）
  const fields = useMemo(() => {
    if (!template) return [];
    try {
      const result = getFieldsFromConfig(template.config);
      console.log(
        '[DynamicSurveyForm] 解析欄位成功，數量:',
        result.length,
        '欄位:',
        result.map(f => ({
          type: f.type,
          label: f.label,
          name: f.name || f.id,
        }))
      );
      return result;
    } catch (err) {
      console.error(
        '[DynamicSurveyForm] 解析欄位失敗:',
        err,
        'config:',
        template?.config
      );
      // 注意：不能在 useMemo 中调用 setError，这会导致问题
      // 错误会在渲染时处理，返回空数组
      return [];
    }
  }, [template?.config]);

  // 判斷是否使用 sections（向後兼容）- 必須在 hooks 之後，條件返回之前
  const useSections = useMemo(() => {
    if (!template) return false;
    return (
      !template.config.fields &&
      !!template.config.sections &&
      template.config.sections.length > 0
    );
  }, [template?.config]);

  // 檢查欄位解析錯誤（在 hooks 之後，條件返回之前）
  useEffect(() => {
    if (template && fields.length === 0 && !useSections) {
      const hasFields =
        !!template.config?.fields && template.config.fields.length > 0;
      const hasSections =
        !!template.config?.sections && template.config.sections.length > 0;
      if (!hasFields && !hasSections) {
        console.error('[DynamicSurveyForm] 模板沒有可用的欄位');
        setError('問卷模板格式錯誤：沒有可用的欄位');
      }
    }
  }, [template, fields.length, useSections]);

  console.log('[DynamicSurveyForm] 渲染狀態:', {
    loading,
    error,
    hasTemplate: !!template,
    templateId: template?.id,
    templateTitle: template?.title,
    fieldsCount: fields.length,
    useSections,
    tenantReady,
    tenantId,
  });

  // 電話改由會員中心填寫，問卷不再顯示或必填手機欄位（由父層帶入）

  if (loading) {
    console.log('[DynamicSurveyForm] 顯示載入中...');
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">載入問卷中...</p>
      </div>
    );
  }

  if (error) {
    console.log('[DynamicSurveyForm] 顯示錯誤:', error);
    return (
      <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg text-center">
        <p className="text-gray-800 text-lg">{error}</p>
      </div>
    );
  }

  if (!template) {
    console.log('[DynamicSurveyForm] 沒有模板，顯示提示');
    return (
      <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg text-center">
        <p className="text-gray-800 text-lg">目前沒有問卷</p>
      </div>
    );
  }

  // 驗證欄位結構
  if (fields.length === 0 && !useSections) {
    console.warn('[DynamicSurveyForm] 沒有可渲染的欄位', {
      hasFields: !!template.config.fields,
      fieldsLength: template.config.fields?.length,
      hasSections: !!template.config.sections,
      sectionsLength: template.config.sections?.length,
      config: template.config,
    });
    return (
      <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
        <p className="text-yellow-800">問卷模板沒有可用的欄位</p>
        <p className="text-xs text-yellow-600 mt-2">請聯繫管理員檢查問卷設定</p>
      </div>
    );
  }

  console.log(
    '[DynamicSurveyForm] 準備渲染表單，欄位數量:',
    fields.length,
    'useSections:',
    useSections
  );

  // 渲染表單（必須在所有 hooks 之後）
  try {
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 驗證錯誤提示 */}
        {validationErrors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <p className="text-red-600 font-semibold mb-2">
              ❌ 請修正以下錯誤：
            </p>
            <ul className="list-disc list-inside text-red-700 space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {useSections ? (
          // 舊格式：使用 sections
          (() => {
            let questionCounter = 0;
            return (template.config.sections || []).map(
              (section, sectionIndex) => {
                try {
                  return (
                    <SurveySection
                      key={sectionIndex}
                      title={section.title}
                      icon={section.icon}
                    >
                      <div className="space-y-4">
                        {section.fields.map((field, fieldIndex) => {
                          try {
                            questionCounter++;
                            const rendered = renderField(
                              field,
                              questionCounter
                            );
                            if (!rendered) {
                              console.warn(
                                '[DynamicSurveyForm] 欄位渲染返回 null:',
                                field
                              );
                            }
                            return rendered;
                          } catch (err) {
                            console.error(
                              '[DynamicSurveyForm] 渲染欄位失敗:',
                              err,
                              field
                            );
                            return (
                              <div
                                key={fieldIndex}
                                className="p-2 bg-red-50 border border-red-200 rounded"
                              >
                                <p className="text-red-600 text-sm">
                                  欄位渲染錯誤:{' '}
                                  {field.label || field.name || field.id}
                                </p>
                              </div>
                            );
                          }
                        })}
                      </div>
                    </SurveySection>
                  );
                } catch (err) {
                  console.error(
                    '[DynamicSurveyForm] 渲染 section 失敗:',
                    err,
                    section
                  );
                  return (
                    <div
                      key={sectionIndex}
                      className="p-4 bg-red-50 border-2 border-red-200 rounded-lg"
                    >
                      <p className="text-red-600">
                        區塊渲染錯誤: {section.title}
                      </p>
                    </div>
                  );
                }
              }
            );
          })()
        ) : (
          // 新格式：扁平化 fields
          <div className="space-y-4">
            {fields.map((field, index) => {
              try {
                const questionNumber = index + 1;
                const rendered = renderField(field, questionNumber);
                if (!rendered) {
                  console.warn(
                    '[DynamicSurveyForm] 欄位渲染返回 null:',
                    field,
                    'index:',
                    index
                  );
                  return (
                    <div
                      key={index}
                      className="p-2 bg-yellow-50 border border-yellow-200 rounded"
                    >
                      <p className="text-yellow-600 text-sm">
                        不支援的欄位類型: {field.type} -{' '}
                        {field.label || field.name || field.id}
                      </p>
                    </div>
                  );
                }
                return rendered;
              } catch (err) {
                console.error(
                  '[DynamicSurveyForm] 渲染欄位失敗:',
                  err,
                  field,
                  'index:',
                  index
                );
                return (
                  <div
                    key={index}
                    className="p-2 bg-red-50 border border-red-200 rounded"
                  >
                    <p className="text-red-600 text-sm">
                      欄位渲染錯誤: {field.label || field.name || field.id} -{' '}
                      {err instanceof Error ? err.message : String(err)}
                    </p>
                  </div>
                );
              }
            })}
          </div>
        )}

        {/* 提交按鈕 */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style={{
              background: template.config.theme?.primaryColor
                ? `linear-gradient(to right, ${template.config.theme.primaryColor}, ${template.config.theme.primaryColor})`
                : undefined,
            }}
          >
            {isSubmitting
              ? '提交中...'
              : template.config.meta?.submitLabel || '✨ 提交問卷'}
          </button>
          <p className="text-center text-sm text-gray-500 mt-3">
            <span className="text-red-500">*</span> 為必填欄位
          </p>
        </div>
      </form>
    );
  } catch (err) {
    console.error(
      '[DynamicSurveyForm] 渲染表單失敗:',
      err,
      'template:',
      template
    );
    return (
      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
        <p className="text-red-600 font-semibold">❌ 渲染問卷表單時發生錯誤</p>
        <p className="text-red-500 text-sm mt-2">
          {err instanceof Error ? err.message : String(err)}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-red-700 underline"
        >
          重新載入頁面
        </button>
      </div>
    );
  }
}
